
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  removeCalculator,
  collapseIframe,
  expandIframe,
  updateCalculatorState,
} from '../../../helpers/helpers';
import { Card } from '@smarterservices/smarter-ui';
import { IconButton, IconArrowOpenUpLine, IconArrowOpenDownLine, CloseButton } from '@smarterservices/smarter-ui';
import { config } from '../../../config';
import { ParamsNotFoundError } from '../../../helpers/definedErrors';

const Calculator = () => {
  const [userId, setUserId] = useState(null);
  const [calculatorType, setCalculatorType] = useState('none');
  const [calculatorState, setCalculatorState] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const calculatorContainerRef = useRef(null);
  const classCalcInitialized = useRef(false);
  const classCalcInstance = useRef(null);

  const renderCalculator = useCallback((type, state = null) => {
    if (!type || !calculatorContainerRef.current) return;

    const disableOtherCalculators = (type) => {
      const { ClassCalc } = window;
      if (!ClassCalc) return;

      // Map of our internal calculator types to ClassCalc's expected types
      const calculatorTypeMap = {
        'four-function-calc': 'basic-calc',
        'scientific-calc': 'scientific-calc',
        'graphing-calc': 'graphing-calc',
        'matrix-calc': 'matrix-calc'
      };

      // Get all ClassCalc calculator types
      const classCalcTypes = ['scientific-calc', 'graphing-calc', 'matrix-calc', 'basic-calc'];

      // Convert our type to ClassCalc's type
      const classCalcType = calculatorTypeMap[type] || type;

      // Get all types to disable (all types except the current one)
      const typesToDisable = classCalcTypes.filter(t => t !== classCalcType);

      const tryDisableButtons = (attempt = 0) => {
        try {
          if (ClassCalc.controller?.disableButtons) {
            ClassCalc.controller.disableButtons(typesToDisable);
          } else if (attempt < 20) {
            setTimeout(() => tryDisableButtons(attempt + 1), 100);
          }
        } catch (error) {
          if (attempt < 20) {
            setTimeout(() => tryDisableButtons(attempt + 1), 100);
          }
        }
      };

      tryDisableButtons();
    };

    // Function to apply calculator state with retry logic
    const applyCalculatorState = (state, attempt = 0) => {
      const { ClassCalc } = window;
      if (!ClassCalc) return;

      try {
        ClassCalc.controller.applyCalculatorState(state);
      } catch (e) {
        if (attempt < 20) {
          setTimeout(() => applyCalculatorState(state, attempt + 1), 500);
        } else {
          console.error('Max retry attempts reached, could not apply calculator state');
        }
      }
    };

    const initCalculator = () => {
      const { ClassCalc } = window;
      if (!ClassCalc) return;

      // Clear previous calculator if it exists
      calculatorContainerRef.current.innerHTML = '';

      // Create a container for the calculator with full height
      const calcContainer = document.createElement('div');
      calcContainer.id = 'calculator';
      calcContainer.style.width = '100%';
      calcContainer.style.height = '100%';
      calcContainer.style.minHeight = '500px'; // Minimum height to ensure calculator is usable
      calculatorContainerRef.current.appendChild(calcContainer);

      // Initialize the calculator
      try {
        const initOptions = {
          apiKey: config.classCalcApiKey,
          userId: userId,
          type: type,
        };

        ClassCalc.init('calculator', initOptions);
        classCalcInstance.current = ClassCalc;

        disableOtherCalculators(type);
        applyCalculatorState(state);

        // Set up polling for calculator state changes
        let lastState = null;
        let pollAttempts = 0;
        const maxPollAttempts = 20;

        const pollState = () => {
          try {
            if (ClassCalc?.controller?.getCalculatorState) {
              const currentState = ClassCalc.controller.getCalculatorState();

              // Only update if state has changed
              if (currentState && JSON.stringify(currentState) !== JSON.stringify(lastState)) {
                lastState = currentState;
                updateCalculatorState(currentState);
                pollAttempts = 0; // Reset attempts on successful update
              }
            } else if (pollAttempts < maxPollAttempts) {
              pollAttempts++;
            } else {
              console.warn('Max poll attempts reached, stopping state polling');
              clearInterval(pollInterval);
            }
          } catch (error) {
            console.error('Error polling calculator state:', error);
            if (pollAttempts >= maxPollAttempts) {
              clearInterval(pollInterval);
            }
            pollAttempts++;
          }
        };

        // Start polling
        const pollInterval = setInterval(pollState, 500);

        // Cleanup on unmount
        return () => clearInterval(pollInterval);
      } catch (error) {
        console.error('Failed to initialize ClassCalc:', error);
      }
    };

    // Load the ClassCalc script if not already loaded
    if (!window.ClassCalc) {
      if (document.getElementById('classCalc-js')) return;

      const classCalcjs = document.createElement('script');
      classCalcjs.id = 'classCalc-js';
      classCalcjs.src = 'https://app.classcalc.com/api';
      classCalcjs.onload = initCalculator;
      document.body.append(classCalcjs);
    } else {
      initCalculator();
    }
  }, [userId]);

  useEffect(() => {
    const initComponent = async () => {
      try {
        const url = new URL(window.location.href);
        const calculatorParams = new Proxy(new URLSearchParams(url.search), {
          get: (searchParams, prop) => searchParams.get(prop),
        });

        if (!calculatorParams || !calculatorParams.userId) {
          throw new ParamsNotFoundError();
        }

        // Handle 'none' values by converting them to null
        const type = calculatorParams.calculatorType === 'none' ? null : calculatorParams.calculatorType;
        const userId = calculatorParams.userId;
        const isExpanded = calculatorParams.isExpanded === 'true';

        // Decode the calculator state from URL
        let state = null;
        const encodedState = calculatorParams.calculatorState;

        if (encodedState && encodedState !== 'none') {
          try {
            state = decodeURIComponent(encodedState);
          } catch (e) {
            console.log('Error parsing calculator state:', e);
            state = null;
          }
        }

        setCalculatorType(type);
        setUserId(userId);
        setCalculatorState(state);
        setIsExpanded(isExpanded);

        if (!isExpanded) {
          collapseIframe('spCalculatorIframeId');
        }
      } catch (error) {
        console.error('Error initializing calculator:', error);
      }
    };

    initComponent();
  }, []);

  useEffect(() => {
    if (isExpanded && calculatorType && !classCalcInitialized.current) {
      renderCalculator(calculatorType, calculatorState);
      classCalcInitialized.current = true;
    }
  }, [isExpanded, calculatorType, calculatorState, renderCalculator]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      collapseIframe('spCalculatorIframeId');
    } else {
      expandIframe('spCalculatorIframeId');
    }
  };

  window.addEventListener('message', (event) => {
    if (event.data.type === 'sp_iframe_expand_state_change') {
      const isExpanded = event.data.isExpanded;
      setIsExpanded(isExpanded);
    }
  });

  // Don't render if we don't have a valid calculator type
  if (!calculatorType) {
    return null;
  }

  return userId ? (
    <Card
      background="secondary"
      borderColor="brand"
      borderWidth="large"
      borderRadius="x-small"
    >
      <Card.Header background="brand">
        <Card.Title>Calculator</Card.Title>
        <Card.Actions>
          {isExpanded ? (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Collapse"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenUpLine />
            </IconButton>
          ) : (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Expand"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenDownLine />
            </IconButton>
          )}
          <CloseButton
            color="primary-inverse"
            screenReaderLabel="Close"
            onClick={removeCalculator}
          />
        </Card.Actions>
      </Card.Header>
      <Card.Body
        padding="none"
        style={{
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          padding: 0
        }}
      >
        <div
          ref={calculatorContainerRef}
          style={{
            height: '100%',
            width: '100%',
            background: '#fff',
            overflow: 'hidden'
          }}
        />
      </Card.Body>
    </Card>
  ) : null;
};

export default Calculator;
